import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React from 'react';
import CashflowCharts from './Components/CashflowCharts';
import DeleteModal from './Components/DeleteModal';
import AddUpdateModal from './Components/FormModal';
import PageHeader from './Components/PageHeader';
import TableControls from './Components/TableControls';
import TransactionsTable from './Components/TransactionsTable';
import {
  BOTH_FILTER,
  EXPENSES_FILTER,
  INCOME_FILTER,
  SEARCH_DELAY,
  VIEWS,
} from './TransactionsConstants';
import { TransactionsPage } from './TransactionsStyles';

class TransactionsView extends React.Component {
  constructor(props) {
    super(props);

    this.fetchNextPage = this.fetchNextPage.bind(this);
    this.fetchFilteredResults = this.fetchFilteredResults.bind(this);
    this.handleQueryChange = this.handleQueryChange.bind(this);
    this.debouncedSearch = debounce(query => this.fetchFilteredResults({ query }), SEARCH_DELAY);

    this.state = { canShowComponent: false };
  }

  componentDidMount() {
    const { currentView, currentRoute, portalName } = this.props;

    document.title = `Transactions | ${portalName}`;

    if (currentView === VIEWS.LIST) {
      const { accountId } = currentRoute.params;

      this.fetchFilteredResults({ accountId: accountId === '0' ? '' : accountId });
    }
  }

  componentDidUpdate() {
    const {
      clientId,
      currentRoute,
      isOrionClient,
      hasLinkedAccounts,
      isFetchingNetworth,
      showTransactionsPage,
    } = this.props;

    const { canShowComponent } = this.state;

    if (isFetchingNetworth) {
      return;
    }

    if (currentRoute.params.accountId === '0' && isOrionClient) {
      ADV.util.Router.go2('AccountPerformanceReporting', {
        clientId,
        tabSlug: 'summary',
        isApex: false,
      });
    } else if (!showTransactionsPage || !hasLinkedAccounts) {
      ADV.util.Router.go2('NetWorth', {
        clientId,
      });
    } else if (!canShowComponent) {
      this.setState({ canShowComponent: true });
    }
  }

  handleQueryChange(query) {
    const { setQuery } = this.props;

    setQuery(query);
    this.debouncedSearch(query);
  }

  fetchNextPage() {
    const {
      pagination: { currentPage, totalPagesCount },
    } = this.props;

    if (totalPagesCount > currentPage) {
      this.fetchFilteredResults({}, currentPage + 1);
    }
  }

  fetchFilteredResults(filters, page = 1) {
    const {
      fetchTransactions,
      clientId,
      currentAccount,
      currentPeriodId,
      currentQuery,
      expenseCategoryFilter,
      selectCategory,
    } = this.props;

    const accountId = isNil(filters.accountId) ? currentAccount.accountId : filters.accountId;
    const periodId = isNil(filters.periodId) ? currentPeriodId : filters.periodId;
    const query = isNil(filters.query) ? currentQuery : filters.query;
    const category = isNil(filters.expenseCategory)
      ? expenseCategoryFilter
      : filters.expenseCategory;
    const incomeExpense = isNil(filters.incomeExpense) ? BOTH_FILTER : filters.incomeExpense;

    selectCategory(category, incomeExpense);
    fetchTransactions(clientId, accountId, periodId, query, category, page, incomeExpense);
  }

  render() {
    const {
      clientId,
      currentPeriodId,
      transactions,
      expenseCategoryOptions,
      periodOptions,
      accounts,
      currentAccount,
      isFilterOpen,
      onFilterOpen,
      onFilterClose,
      openAddUpdateModal,
      setDraftTransaction,
      currentQuery,
      pagination: { currentPage, totalPagesCount },
      expenseCategoryFilter,
      expensesData,
      incomeData,
      updateTransaction,
      incomeExpense,
    } = this.props;

    const { canShowComponent } = this.state;

    if (!canShowComponent) {
      return null;
    }

    return (
      <TransactionsPage>
        <PageHeader />

        <CashflowCharts
          expensesData={expensesData}
          selectExpensesCategory={categoryName => {
            if (categoryName === expensesData.title) {
              this.fetchFilteredResults({
                expenseCategory: '',
                incomeExpense: EXPENSES_FILTER,
              });
            } else {
              this.fetchFilteredResults({
                expenseCategory: categoryName,
                incomeExpense: EXPENSES_FILTER,
              });
            }
          }}
          incomeChart={incomeData}
          selectIncomeCategory={categoryName => {
            if (categoryName === incomeData.title) {
              this.fetchFilteredResults({
                expenseCategory: '',
                incomeExpense: INCOME_FILTER,
              });
            } else {
              this.fetchFilteredResults({
                expenseCategory: categoryName,
                incomeExpense: INCOME_FILTER,
              });
            }
          }}
        />

        <TableControls
          onFilterOpen={onFilterOpen}
          onFilterClose={onFilterClose}
          expenseCategoryOptions={expenseCategoryOptions}
          query={currentQuery}
          periodOptions={periodOptions}
          currentPeriodId={currentPeriodId}
          onAddTransactionClick={openAddUpdateModal}
          onPeriodChange={period => this.fetchFilteredResults({ periodId: period.value })}
          onQueryChange={this.handleQueryChange}
          currentExpenseCategoryFilter={expenseCategoryFilter}
          onExpenseCategoryFilterChange={({ value: expenseCategory }) => {
            this.fetchFilteredResults({ expenseCategory });
          }}
          currentAccount={currentAccount}
          accounts={accounts}
          onAccountChange={account =>
            ADV.util.Router.go2('Transactions', { clientId, accountId: account.value })
          }
        />

        <TransactionsTable
          isFilterOpen={isFilterOpen}
          expenseCategoryOptions={expenseCategoryOptions}
          transactions={transactions}
          currentPage={currentPage}
          totalPagesCount={totalPagesCount}
          fetchNextPage={this.fetchNextPage}
          onExpenseCategoryChange={(transaction, categoryId) => {
            updateTransaction(
              clientId,
              { ...transaction, Category: categoryId },
              currentAccount.accountId,
              currentPeriodId,
              currentQuery,
              expenseCategoryFilter,
              currentPage,
              incomeExpense,
            );
          }}
          onEditTransactionClick={transaction => {
            setDraftTransaction(transaction);
            openAddUpdateModal();
          }}
          currentAccount={currentAccount}
          currentPeriodId={currentPeriodId}
        />
        <AddUpdateModal />
        <DeleteModal />
      </TransactionsPage>
    );
  }
}

TransactionsView.propTypes = {
  openAddUpdateModal: PropTypes.func.isRequired,
  expensesData: PropTypes.shape({}).isRequired,
  incomeData: PropTypes.shape({}).isRequired,
  isOrionClient: PropTypes.bool.isRequired,
  showTransactionsPage: PropTypes.bool.isRequired,
  isFetchingNetworth: PropTypes.bool.isRequired,
  hasLinkedAccounts: PropTypes.bool.isRequired,
  portalName: PropTypes.string.isRequired,
};

export default TransactionsView;
